import { FC, ReactElement, ReactNode, useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import ActivityFrame from "../../../components/activity-frame/activity-frame";
import Breadcrumb from "../../../components/breadcrumb/breadcrumb";
import Tabs, { getTabsListFromLabels } from "../../../components/tabs/tabs";
import MessagesTab from "../../../containers/messages-tab";
import { useLocale } from "../../../contexts/localeContext";
import Restricted from "../../../contexts/PermissionProvider/RestrictedWrapper";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { Button } from "../../../components/button/Button";
import plusIcon from "../../../images/Plus.svg";
import { Category } from "../../../models/Category";
import { MESSAGE_TYPES } from "@/constants/messages-constants";
import { toast } from "react-toastify";
import { getSystemStats } from "@/services/systemService";
import { getCountsCategory, MESSAGE_TYPE, TYPE_PER_MESSAGE_TYPE } from "@/constants/system-constants";
import { showError } from "@/services/utilities/ServiceError";
import SystemStat from "@/models/SystemStat";

interface MessagesProp {
    selected: number;
    setSelected: (index: number) => void;
}

const Messages: FC<MessagesProp> = ({ selected, setSelected }) => {
    const [busy, setBusy] = useState(false);
    const history = useHistory();
    const { translate } = useLocale();
    const [categories, setCategories] = useState<Category[]>([
        {
            displayName: translate.byKey(TranslationKeys.all),
            heading: translate.byKey(TranslationKeys.messages),
            value: "",
            id: 1,
            count: 0,
        },
        {
            displayName: translate.byKey(TranslationKeys.general),
            heading: translate.byKey(TranslationKeys.general),
            value: "",
            id: 2,
            count: 0
        },
        {
            displayName: translate.byKey(TranslationKeys.biometrics),
            heading: translate.byKey(TranslationKeys.biometrics),
            value: "",
            id: 3,
            count: 0
        }
    ])
    const [tabContent, setTabContent] = useState<ReactNode[]>([]);
    const getItemPath = (selected: number) => {
        switch (selected) {
            case 0:
                return "/access-control/configuration/message/";
            case 1:
                return "/access-control/configuration/general/";
            case 2:
                return "/access-control/configuration/biometric-message/";
            default:
                break;
        }
    }

    useEffect(() => {
        getCategoriesTotals();
    }, []);

    const getCategoriesTotals = async () => {
        try {
            setBusy(true);
            const showTabCounts = window.sessionStorage.getItem("showTabCounts") === "1";
            const systemStats: SystemStat | undefined = showTabCounts ? await getSystemStats(TYPE_PER_MESSAGE_TYPE) : undefined;

            const updatedCategories = categories.map(category => {
                if (category.id === 1) {
                    return { ...category, count: systemStats ? getCountsCategory(systemStats, MESSAGE_TYPE.GENERAL) + getCountsCategory(systemStats, MESSAGE_TYPE.BIOMETRICS) : 0 };
                } else if (category.id === 2) {
                    return { ...category, count: systemStats ? getCountsCategory(systemStats, MESSAGE_TYPE.GENERAL) : 0 };
                } else if (category.id === 3) {
                    return { ...category, count: systemStats ? getCountsCategory(systemStats, MESSAGE_TYPE.BIOMETRICS) : 0 };
                }
                return category;
            });

            setCategories(updatedCategories);
        } catch (error) {
            showError(error, toast.warn);
        } finally {
            setBusy(false);
        }
    }

    useEffect(() => {
        if (categories.length > 0) {
            const newTabContent: ReactElement[] = [
                <MessagesTab
                    key={"allMessages"} 
                    setBusy={setBusy}
                    getCategoriesTotals={getCategoriesTotals}
                    messageType={MESSAGE_TYPES.ALL} />,
                <MessagesTab
                    key={"generalMessages"} 
                    setBusy={setBusy} 
                    getCategoriesTotals={getCategoriesTotals}
                    messageType={MESSAGE_TYPES.GENERAL}/>,
                <MessagesTab
                    key={"biometricMessages"} 
                    setBusy={setBusy}
                    getCategoriesTotals={getCategoriesTotals}
                    messageType={MESSAGE_TYPES.BIOMETRIC}/>
            ];
            setTabContent(newTabContent);
        }
    }, [categories]);

    return (
        <>
            <ActivityFrame
                busy={busy}
                className="flex flex-grow overflow-y-hidden">
                <div id="container-tabs" className="pt-4 pl-4 pb-4 flex flex-col flex-grow ">
                    <Breadcrumb
                        path={[
                            {
                                value: "/access-control/configuration",
                                displayName: translate.byKey(TranslationKeys.configuration),
                                disableClick: true
                            },
                            {
                                value: "/access-control/configuration/messages",
                                displayName: categories[selected]?.heading,
                            },
                        ]}
                        onClick={(item) => history.push(item.value)} />
                    <Tabs
                        className="overflow-y-hidden"
                        tabs={getTabsListFromLabels(categories)}
                        onSelect={(i) => setSelected(i)}
                        selected={selected}
                        onAdd={() => { }}
                        add={false}
                        tabStyle={{}}>
                        {tabContent[selected]}
                        <div className="flex flex-col flex-grow overflow-y-hidden mb-viewport">
                            <div className="flex flex-row justify-end button-cluster">
                                <Restricted to={`add.messages.element`}>
                                    <Link to={{ pathname: getItemPath(selected) }}>
                                        <Button variant="submit" shape="rounded" id="addMessagesButton">
                                            <img
                                                src={plusIcon}
                                                className="px-2 w-8 py-1"
                                                alt="" />
                                            <span className="mr-2">{translate.byKey(TranslationKeys.add)}</span>
                                        </Button>
                                    </Link>
                                </Restricted>
                            </div>
                        </div>
                    </Tabs>
                </div>
            </ActivityFrame>
        </>
    );
};

export default Messages;
